import { css, CSSResultGroup, html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators.js";

import {
  AttachInternals,
  customElement,
  DesignToken,
  Dirty,
  Disabled,
  FormAssociated,
  formValue,
  Labelled,
  Role,
  setCustomState,
  Touched,
} from "@m3e/web/core";

import { SelectionManager, selectionManager } from "@m3e/web/core/a11y";
import { M3eDirectionality } from "@m3e/web/core/bidi";

import { M3eButtonSegmentElement } from "./ButtonSegmentElement";

/**
 * A button that allows a user to select from a limited set of options.
 *
 * @description
 * The `m3e-segmented-button` component allows users to select one or more options from a horizontal group.
 * Each segment behaves like a toggle-able button, supporting icon and label content, selection state, and
 * accessibility roles. Built with Material Design 3 principles, it adapts shape, color, and ripple feedback
 * based on interaction state and input modality. Segments are visually unified but independently interactive.
 *
 * @example
 * The following example illustrates a single-select segmented button with four segments.
 * ```html
 * <m3e-segmented-button>
 *  <m3e-button-segment checked>8 oz</m3e-button-segment>
 *  <m3e-button-segment>12 oz</m3e-button-segment>
 *  <m3e-button-segment>16 oz</m3e-button-segment>
 *  <m3e-button-segment>20 oz</m3e-button-segment>
 * </m3e-segmented-button>
 * ```
 * @example
 * The next example illustrates a multiselect segmented button designated using the `multi` attribute.
 * ```html
 * <m3e-segmented-button multi>
 *  <m3e-button-segment checked>$</m3e-button-segment>
 *  <m3e-button-segment checked>$$</m3e-button-segment>
 *  <m3e-button-segment>$$$</m3e-button-segment>
 *  <m3e-button-segment>$$$$</m3e-button-segment>
 * </m3e-segmented-button>
 * ```
 *
 * @tag m3e-segmented-button
 *
 * @slot - Renders the segments of the button.
 *
 * @attr disabled - Whether the element is disabled.
 * @attr hide-selection-indicator - Whether to hide the selection indicator.
 * @attr multi - Whether multiple options can be selected.
 * @attr name - The name that identifies the element when submitting the associated form.
 *
 * @fires beforeinput - Dispatched before the checked state of a segment changes.
 * @fires input - Dispatched when the checked state of a segment changes.
 * @fires change - Dispatched when the checked state of a segment changes.
 *
 * @cssprop --m3e-segmented-button-start-shape - Border radius for the first segment in a segmented button.
 * @cssprop --m3e-segmented-button-end-shape - Border radius for the last segment in a segmented button.
 */
@customElement("m3e-segmented-button")
export class M3eSegmentedButtonElement extends Labelled(
  Dirty(Touched(FormAssociated(Disabled(AttachInternals(Role(LitElement, "radiogroup")))))),
) {
  /** The styles of the element. */
  static override styles: CSSResultGroup = css`
    :host {
      display: inline-flex;
      vertical-align: middle;
      align-items: center;
    }
    :host([hidden]) {
      display: none;
    }
    ::slotted(:is(:state(--first), :--first)) {
      border-start-start-radius: var(--m3e-segmented-button-start-shape, ${DesignToken.shape.corner.full});
      border-end-start-radius: var(--m3e-segmented-button-start-shape, ${DesignToken.shape.corner.full});
    }
    ::slotted(:is(:state(--last), :--last)) {
      border-start-end-radius: var(--m3e-segmented-button-end-shape, ${DesignToken.shape.corner.full});
      border-end-end-radius: var(--m3e-segmented-button-end-shape, ${DesignToken.shape.corner.full});
    }
    ::slotted(:not(:is(:state(--first), :--first))) {
      --_segmented-button-left-border: none;
    }
  `;

  /** @private */ #directionalitySubscription?: () => void;

  /** @internal */
  readonly [selectionManager] = new SelectionManager<M3eButtonSegmentElement>()
    .withWrap()
    .withDirectionality(M3eDirectionality.current)
    .onActiveItemChange(() => this[selectionManager].activeItem?.click());

  /**
   * Whether multiple options can be selected.
   * @default false
   */
  @property({ type: Boolean }) multi = false;

  /**
   * Whether to hide the selection indicator.
   * @default false
   */
  @property({ attribute: "hide-selection-indicator", type: Boolean }) hideSelectionIndicator = false;

  /** The segments of the button. */
  get segments(): readonly M3eButtonSegmentElement[] {
    return this[selectionManager]?.items ?? [];
  }

  /** The selected segment(s) of the button. */
  get selected(): readonly M3eButtonSegmentElement[] {
    return this[selectionManager]?.selectedItems ?? [];
  }

  /** The selected (enabled) value(s) of the button. */
  get value(): string | readonly string[] | null {
    const values = this.selected.filter((x) => !x.disabled).map((x) => x.value);
    switch (values.length) {
      case 0:
        return null;
      case 1:
        return values[0];
      default:
        return values;
    }
  }

  /** @inheritdoc @internal */
  override get [formValue]() {
    const values = this.value;
    if (Array.isArray(values)) {
      const data = new FormData();
      for (const value of values) {
        data.append(this.name, value);
      }
      return data;
    }
    return <string | null>values;
  }

  /** @inheritdoc */
  override connectedCallback(): void {
    super.connectedCallback();

    this[selectionManager].multi = this.multi;
    this[selectionManager].disableRovingTabIndex(this.multi);
    this.#directionalitySubscription = M3eDirectionality.observe(
      () => (this[selectionManager].directionality = M3eDirectionality.current),
    );
  }

  /** @inheritdoc */
  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#directionalitySubscription?.();
  }

  /** @inheritdoc */
  protected override update(changedProperties: PropertyValues<this>): void {
    super.update(changedProperties);

    if (changedProperties.has("disabled") && (changedProperties.get("disabled") !== undefined || this.disabled)) {
      this[selectionManager].disabled = this.disabled;
    }

    if (changedProperties.has("multi")) {
      this.role = this.multi ? "group" : "radiogroup";
      this[selectionManager].multi = this.multi;
      this[selectionManager].disableRovingTabIndex(this.multi);
      this[selectionManager].items.forEach((x) => {
        if (this.multi) {
          x.role = "button";
          x.ariaPressed = `${x.checked}`;
          x.ariaSelected = x.ariaChecked = null;
        } else {
          x.role = "radio";
          x.ariaChecked = `${x.checked}`;
          x.ariaPressed = x.ariaSelected = null;
        }
      });
    }

    if (changedProperties.has("multi") || changedProperties.has("disabled")) {
      // aria-disabled is not supported on radiogroup roles.
      this.ariaDisabled = this.multi && this.disabled ? "true" : null;
    }

    if (changedProperties.has("hideSelectionIndicator")) {
      this.segments.forEach((x) => setCustomState(x, "--hide-selection", this.hideSelectionIndicator));
    }
  }

  /** @inheritdoc */
  protected override render(): unknown {
    return html`<slot
      @slotchange=${this.#handleSlotChange}
      @keydown=${this.#handleKeyDown}
      @change=${this.#handleChange}
    ></slot>`;
  }

  /** @private */
  #handleSlotChange() {
    const { added } = this[selectionManager].setItems([...this.querySelectorAll("m3e-button-segment")]);
    added.forEach((x) => setCustomState(x, "--hide-selection", this.hideSelectionIndicator));
    this[selectionManager].items.forEach((segment, i) => {
      setCustomState(segment, "--first", i == 0);
      setCustomState(segment, "--last", i == this[selectionManager].items.length - 1);
    });
  }

  /** @private */
  #handleKeyDown(e: KeyboardEvent): void {
    if (!this.multi) {
      this[selectionManager].onKeyDown(e);
    }
  }

  /** @private */
  #handleChange(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "m3e-segmented-button": M3eSegmentedButtonElement;
  }
}
